import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  TCreateDeviceInputSchema,
  TCreateDeviceSchema,
} from '@/console/features/devices/components/create-device/create-device.types';
import {
  flattenCredentialMetaData,
  getCredentialInputKeys,
} from '@/console/features/devices/utils/credential-meta-data';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { useDefineAssetTags } from '@/shared/features/tags/hooks/use-define-asset-tags/use-define-asset-tags';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { useCreateDeviceMutation } from '@/shared/redux/api/assets/devices/devices-api';
import { useLinkDeviceToGatewayMutation } from '@/shared/redux/api/assets/links/links-api';
import { AssetTypeEnum } from '@/shared/types/types.generated';

export const useCreateDevice = (groupId?: string, handleClose?: () => void) => {
  const { t } = useTranslation();
  const [createDevice] = useCreateDeviceMutation();
  const [linkDevicesToGateway] = useLinkDeviceToGatewayMutation();
  const { getDevice } = useVendor();
  const [devicePort, setDevicePort] = useState<number>(0);
  const defineAssetTags = useDefineAssetTags();

  const handleDeviceConfiguration = async (
    data: TSubmittedData<Partial<TCreateDeviceSchema>>,
  ) => {
    const { vendorId, ideConfigId, deviceType } =
      data.data as TCreateDeviceInputSchema;
    const device = getDevice(vendorId, ideConfigId, deviceType!);

    if (device) {
      setDevicePort(device.port);
    }
  };

  const handleSubmit = async (data: TSubmittedData<TCreateDeviceSchema>[]) => {
    const deviceData = data[0].data;
    const connectionData = data[1].data;

    const {
      tags,
      vaultId: _,
      groupId: deviceGroupId,
      ...deviceRest
    } = deviceData;
    const { vendorId, ideConfigId, deviceType } =
      deviceData as TCreateDeviceInputSchema;
    const credentialKeys = getCredentialInputKeys(
      getDevice(vendorId, ideConfigId, deviceType)?.additionalInputs,
    );
    const {
      connectionConfiguration,
      gatewayId,
      enableSmb,
      enableFtp,
      ftpConfiguration: rawFtpConfig,
      smbConfiguration: rawSmbConfig,
    } = connectionData;

    const getFtpConfig = () => {
      if (!rawFtpConfig || !enableFtp) return undefined;
      const { vaultId: _, ...config } = rawFtpConfig;
      return config;
    };

    const getSmbConfig = () => {
      if (!rawSmbConfig || !enableSmb) return undefined;
      const { vaultId: _, ...config } = rawSmbConfig;
      return config;
    };

    const device = {
      ...deviceRest,
      metaData: flattenCredentialMetaData(
        (deviceRest as { metaData?: Record<string, unknown> }).metaData,
        credentialKeys,
      ),
      groupId: (deviceGroupId ? deviceGroupId : undefined) ?? groupId,
      connectionConfiguration,
      ftpConfiguration: getFtpConfig(),
      smbConfiguration: getSmbConfig(),
    };

    try {
      const response = await createDevice({ device }).unwrap();

      if (tags) {
        await defineAssetTags({
          tags,
          assetId: response.createDevice.deviceId,
          assetType: AssetTypeEnum.Device,
        });
      }

      if (gatewayId) {
        await linkDevicesToGateway({
          gatewayId,
          deviceId: response.createDevice.deviceId,
        });
      }

      toast.success(t('device:create_success'));
      handleClose?.();

      return response.createDevice;
    } catch {
      toast.error(t('device:create_error'));
    }
  };

  return {
    devicePort,
    handleDeviceConfiguration,
    handleSubmit,
  };
};
